// src/features/subscription/AddOnFormDialog.tsx
// Create / edit a subscription add-on — one form-kit dialog shared by both
// flows (docs/ux/14-forms.md), opened from AddOnListPage's "+ New" and from
// AddOnDetailPage's "Edit" button. Slug is derived from the name and locked
// once the add-on exists (BookingTypeDialog precedent). Price is
// entered/displayed in major units and converted to minor units on submit,
// same convention as GrantCreditModal.

import { useEffect, useMemo, useState } from 'react';
import { observer } from '@react';
import { useSubscriptionAddon, useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow, SegmentedControl } from '@/components/form';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { ChipInput } from './ChipInput';
import { errMessage, toMajorUnitsText, toMinorUnits } from './format';
import type { AddOn } from '@core/types/subscription/AddOn';
import type { AddOnType } from '@core/types/subscription/AddOnType';
import type { Currency } from '@core/types/subscription/Currency';
import type { BillingCycle } from '@core/types/subscription/BillingCycle';

const ADDON_TYPES: AddOnType[] = ['ADDON_TYPE_MODULE', 'ADDON_TYPE_SEAT_PACK', 'ADDON_TYPE_USAGE_PACK', 'ADDON_TYPE_SUPPORT_TIER'];
const CURRENCIES: Currency[] = ['CURRENCY_USD', 'CURRENCY_EUR', 'CURRENCY_RUB', 'CURRENCY_GBP', 'CURRENCY_JPY', 'CURRENCY_CNY', 'CURRENCY_KZT', 'CURRENCY_BYN', 'CURRENCY_AED', 'CURRENCY_TRY'];
const BILLING_CYCLES: BillingCycle[] = ['BILLING_CYCLE_MONTHLY', 'BILLING_CYCLE_QUARTERLY', 'BILLING_CYCLE_SEMI_ANNUALLY', 'BILLING_CYCLE_ANNUALLY', 'BILLING_CYCLE_DAILY', 'BILLING_CYCLE_WEEKLY', 'BILLING_CYCLE_BIWEEKLY', 'BILLING_CYCLE_CUSTOM'];

function decimalText(v: string): string | null {
  return /^\d*\.?\d{0,2}$/.test(v) ? v : null;
}

function slugify(v: string): string {
  return v.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

interface FormState {
  name: string;
  description: string;
  slug: string;
  addon_type: AddOnType;
  price_amount: string; // major units text
  currency: Currency;
  billing_cycle: BillingCycle;
  is_one_time: boolean;
  is_public: boolean;
  is_active: boolean;
  compatible_plan_slugs: string[];
  required_plan_slugs: string[];
  entitlement_keys: string[];
}

function snapshotOf(addon: AddOn | null): FormState {
  return {
    name: addon?.name ?? '',
    description: addon?.description ?? '',
    slug: addon?.slug ?? '',
    addon_type: addon?.addon_type ?? 'ADDON_TYPE_MODULE',
    price_amount: toMajorUnitsText(addon?.price_amount),
    currency: addon?.currency ?? 'CURRENCY_USD',
    billing_cycle: addon?.billing_cycle ?? 'BILLING_CYCLE_MONTHLY',
    is_one_time: addon?.is_one_time ?? false,
    is_public: addon?.is_public ?? true,
    is_active: addon?.is_active ?? true,
    compatible_plan_slugs: addon?.compatible_plan_slugs ?? [],
    required_plan_slugs: addon?.required_plan_slugs ?? [],
    entitlement_keys: addon?.entitlement_keys ?? [],
  };
}

export interface AddOnFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Add-on being edited; null → create. */
  addon: AddOn | null;
  onSaved: () => void;
}

export const AddOnFormDialog = observer(({ open, onClose, addon, onSaved }: AddOnFormDialogProps) => {
  const { t } = useI18n();
  const store = useSubscriptionAddon();
  const isEdit = !!addon;

  const [form, setForm] = useState<FormState>(() => snapshotOf(addon));
  const [initial, setInitial] = useState<FormState>(() => snapshotOf(addon));
  const [slugTouched, setSlugTouched] = useState(false);
  const [moreFields, setMoreFields] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const snap = snapshotOf(addon);
    setForm(snap);
    setInitial(snap);
    setSlugTouched(isEdit);
    setMoreFields(false);
    setError(null);
  }, [open, addon]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);
  const canSubmit = form.name.trim().length > 0 && form.slug.trim().length > 0;

  const typeOptions = ADDON_TYPES.map((v) => ({ value: v, label: t(`subscription.addon.type.${v.replace('ADDON_TYPE_', '').toLowerCase()}`) }));
  const currencyOptions = CURRENCIES.map((v) => ({ value: v, label: t(`subscription.currency.${v.replace('CURRENCY_', '').toLowerCase()}`) }));
  const billingCycleOptions = BILLING_CYCLES.map((v) => ({ value: v, label: t(`subscription.billingCycle.${v.replace('BILLING_CYCLE_', '').toLowerCase()}`) }));

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (isEdit && addon) {
        await store.UpdateAddOn(addon.entity?.id ?? '', {
          addon: {
            ...addon,
            name: form.name.trim(),
            description: form.description,
            slug: form.slug.trim(),
            addon_type: form.addon_type,
            price_amount: toMinorUnits(form.price_amount),
            currency: form.currency,
            billing_cycle: form.billing_cycle,
            is_one_time: form.is_one_time,
            is_public: form.is_public,
            is_active: form.is_active,
            compatible_plan_slugs: form.compatible_plan_slugs,
            required_plan_slugs: form.required_plan_slugs,
            entitlement_keys: form.entitlement_keys,
          },
          field_mask: ['name', 'description', 'slug', 'addon_type', 'price_amount', 'currency', 'billing_cycle', 'is_one_time', 'is_public', 'is_active', 'compatible_plan_slugs', 'required_plan_slugs', 'entitlement_keys'],
        });
      } else {
        await store.CreateAddOn({
          name: form.name.trim(),
          description: form.description,
          slug: form.slug.trim(),
          addon_type: form.addon_type,
          price_amount: toMinorUnits(form.price_amount),
          currency: form.currency,
          billing_cycle: form.billing_cycle,
          is_one_time: form.is_one_time,
          is_public: form.is_public,
          compatible_plan_slugs: form.compatible_plan_slugs,
          required_plan_slugs: form.required_plan_slugs,
          entitlement_keys: form.entitlement_keys,
        });
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(errMessage(e, t('subscription.addon.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={isEdit ? t('common.saveChanges') : t('subscription.addon.create')}
      canSubmit={canSubmit}
      isSubmitting={saving}
      dirty={dirty}
      width="lg"
      aria-label={isEdit ? t('subscription.addon.edit') : t('subscription.addon.create')}
      footerLeft={
        <button type="button" onClick={() => setMoreFields((v) => !v)} className="text-xs text-muted-foreground hover:text-foreground">
          {moreFields ? t('formKit.fewerFields') : t('formKit.moreFields')}
        </button>
      }
    >
      <div className="space-y-1 px-3.5 pb-2 pt-2">
        {error && <Alert variant="error">{error}</Alert>}
        <TitleInput
          value={form.name}
          onChange={(v) => { set('name', v); if (!slugTouched && !isEdit) set('slug', slugify(v)); }}
          placeholder={`${t('subscription.addon.field.name')}…`}
          initialFocus
          aria-label={t('subscription.addon.field.name')}
        />
        <AutoGrowTextarea
          value={form.description}
          onChange={(v) => set('description', v)}
          placeholder={`${t('subscription.addon.field.description')}…`}
          minRows={1}
        />
      </div>

      <div className="px-3.5 pb-2">
        <FormRow label={t('subscription.addon.field.slug')} labelWidth="sm">
          <input
            value={form.slug}
            onChange={(e) => { setSlugTouched(true); set('slug', slugify(e.target.value)); }}
            disabled={isEdit}
            aria-label={t('subscription.addon.field.slug')}
            className="w-full max-w-xs rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm text-foreground outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
        </FormRow>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-3.5 pb-3">
        <SegmentedControl
          size="sm"
          value={form.addon_type}
          onChange={(v) => set('addon_type', v as AddOnType)}
          options={typeOptions}
          aria-label={t('subscription.addon.field.type')}
        />
        <div className="w-28">
          <Select value={form.currency} onChange={(e) => set('currency', e.target.value as Currency)} options={currencyOptions} aria-label={t('subscription.addon.field.currency')} fullWidth />
        </div>
        <div className="w-36">
          <Select value={form.billing_cycle} onChange={(e) => set('billing_cycle', e.target.value as BillingCycle)} options={billingCycleOptions} aria-label={t('subscription.addon.field.billingCycle')} fullWidth />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 px-3.5 pb-3">
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-muted px-2.5 py-1">
          <span className="text-xs text-muted-foreground">{t('subscription.addon.field.price')}</span>
          <input
            type="text"
            inputMode="decimal"
            value={form.price_amount}
            onChange={(e) => { const v = decimalText(e.target.value); if (v !== null) set('price_amount', v); }}
            placeholder="0.00"
            aria-label={t('subscription.addon.field.price')}
            className="w-20 bg-transparent text-right text-sm tabular-nums text-foreground outline-none"
          />
          <span className="text-xs text-muted-foreground">{form.currency.replace('CURRENCY_', '')}</span>
        </span>
        <label className="flex items-center gap-1.5 text-sm text-foreground">
          <input type="checkbox" checked={form.is_one_time} onChange={(e) => set('is_one_time', e.target.checked)} className="h-4 w-4 rounded border-input" />
          {t('subscription.addon.field.isOneTime')}
        </label>
        <label className="flex items-center gap-1.5 text-sm text-foreground">
          <input type="checkbox" checked={form.is_public} onChange={(e) => set('is_public', e.target.checked)} className="h-4 w-4 rounded border-input" />
          {t('subscription.addon.field.isPublic')}
        </label>
      </div>

      {moreFields && (
        <div className="space-y-3 border-t border-border px-3.5 py-3">
          <FormRow label={t('subscription.addon.field.compatiblePlans')} labelWidth="sm" hint={t('subscription.addon.field.compatiblePlansHint')}>
            <ChipInput value={form.compatible_plan_slugs} onChange={(v) => set('compatible_plan_slugs', v)} placeholder={t('subscription.addon.field.slugsPlaceholder')} aria-label={t('subscription.addon.field.compatiblePlans')} />
          </FormRow>
          <FormRow label={t('subscription.addon.field.requiredPlans')} labelWidth="sm" hint={t('subscription.addon.field.requiredPlansHint')}>
            <ChipInput value={form.required_plan_slugs} onChange={(v) => set('required_plan_slugs', v)} placeholder={t('subscription.addon.field.slugsPlaceholder')} aria-label={t('subscription.addon.field.requiredPlans')} />
          </FormRow>
          <FormRow label={t('subscription.addon.field.entitlementKeys')} labelWidth="sm">
            <ChipInput value={form.entitlement_keys} onChange={(v) => set('entitlement_keys', v)} placeholder={t('subscription.addon.field.entitlementKeysPlaceholder')} aria-label={t('subscription.addon.field.entitlementKeys')} />
          </FormRow>
          {isEdit && (
            <ToggleSwitch label={t('subscription.addon.field.isActive')} checked={form.is_active} onChange={(v) => set('is_active', v)} />
          )}
        </div>
      )}
    </FormDialog>
  );
});

export default AddOnFormDialog;
